<template>
  <div v-show="mainGraph" class="full-page" id="main-graph-scroll">
    <b-icon-x class="close-icon" @click="closePage('mainGraph')"></b-icon-x>
    <div class="container-fluid">
      <h2 style="margin-top: 1em;">Advanced</h2>

      <data-set-header />

      <div class="main-grid-cont">
        <div class="svg-cont">
          <svg id="main-graph-dates" class="date-lines" height="55" :class="{ 'date-lines-fixed': fixed }">
            <text v-for="d of dateLines" :key="d.id" style="font: 12px sans-serif;" :x="d.x" y="55" :transform="`rotate(-45 ${d.x} 55)`">
              {{ d.text }}
            </text>
            <g v-if="showMoon">
              <g
                :transform="`scale(.12)translate(${d.x / 0.12 - 190},320)`"
                v-for="(d, i) of dateLines"
                :key="`${i}-moon`"
                v-html="moonIcon(d).icon"
              ></g>
            </g>
          </svg>
          <svg width="78%" class="product-graph" height="30">
            <product-graph :rowHeight="svgRowHeight" :uniqueDates="uniqueDates" :windowWidth="windowWidth" />
          </svg>
          <svg id="main-svg" width="100%" :height="svgRowHeight * groups.length + 100">
            <!-- <text v-for="d of dateLines" :key="d.id" style="font: 12px sans-serif;" :x="d.x" y="55" :transform="`rotate(-45 ${d.x} 55)`" >{{d.text}}</text> -->
            <g v-for="(row, i) of groups" :key="row.id" :transform="`translate(0,${graphRows(i, 'noHead')})`">
              <text style="font:  bold 15px sans-serif;" x="20" y="30">{{ row.title }}</text>

              <!-- ranges -->
              <g v-if="row.settings.absolute && row.settings.ranges">
                <rect x="150" :y="ranges(row).veryy" :width="windowWidth" :height="ranges(row).veryh" fill="#e6f1fd" />
                <rect x="150" :y="ranges(row).oky" :width="windowWidth" :height="ranges(row).okh" fill="#c5e1ff" />
                <line
                  x1="150"
                  :x2="windowWidth"
                  :y1="ranges(row).perfect"
                  :y2="ranges(row).perfect"
                  style="stroke:black;stroke-width: 1"
                  stroke-dasharray="4,4"
                />
              </g>

              <line x1="18" :x2="windowWidth" :y1="svgRowHeight - 2" :y2="svgRowHeight - 2" style="stroke:#888;stroke-width:.5" />
              <line x1="18" :x2="windowWidth" :y1="svgRowHeight" :y2="svgRowHeight" style="stroke:#888;stroke-width:.5" />

              <line
                v-for="d of dateLines"
                :key="`${d.id}-${i}`"
                :x1="d.x"
                :x2="d.x"
                y1="0"
                :y2="svgRowHeight"
                style="stroke:#ccc;stroke-width:.5; stroke-dasharray:4,5"
              />

              <path v-for="path of groupLines(row)" :key="path.id" :style="path.style" fill="none" stroke-width="3" :d="path.data" />

              <!-- data points -->
              <g v-for="c of elementCircles(row)" :key="c.id">
                <rect
                  :x="c.circleX + textOffset(c.index, c.text)"
                  :y="c.circleY - 6.8"
                  height="9"
                  :width="c.text.length * 7"
                  fill="white"
                  style="opacity: .5"
                />

                <circle
                  :cx="c.circleX"
                  :cy="c.circleY"
                  r="5"
                  :fill="c.color"
                  style="stroke:black;stroke-width:1.4"
                  v-if="c.icon === 'circle' || !c.icon"
                ></circle>

                <rect
                  :x="c.circleX - 4.5"
                  :y="c.circleY - 4.5"
                  height="9"
                  width="9"
                  :fill="c.color"
                  style="stroke:black;stroke-width:1.4"
                  v-if="c.icon === 'rect'"
                ></rect>

                <svg v-if="c.icon === 'triangle'" :x="c.circleX - 6" :y="c.circleY - 6">
                  <polygon points="6,0 0,12 12,12" :fill="c.color" style="stroke:black;stroke-width:1.4" />
                </svg>

                <svg v-if="c.icon === 'diamond'" :x="c.circleX - 6" :y="c.circleY - 6">
                  <polygon points="6,0 0,6 6,12 12,6" :fill="c.color" style="stroke:black;stroke-width:1.4" />
                </svg>

                <svg v-if="c.icon === 'star'" :x="c.circleX - 10" :y="c.circleY - 10">
                  <polygon points="10,1 4,19.8 19,7.8 1,7.8 16,19.8" :fill="c.color" />
                </svg>
                <text style="font: bold 10.5px sans-serif;" :x="c.circleX + textOffset(c.index, c.text)" :y="c.circleY">{{ c.text }}</text>
              </g>

              <!-- legend -->
              <g v-for="(line, i) of legend(row)" :key="line.id" v-show="line.visible">
                <text style="font: 11px sans-serif;" x="20" :y="55 + i * 20">{{ line.legend }}</text>
                <line x1="20" x2="120" :y1="60 + i * 20" :y2="60 + i * 20" :style="line.style" stroke-width="3" />
              </g>
            </g>
          </svg>
        </div>

        <div class="settings">
          <div class="setting-nav">
            <!-- <b-icon-plus @click="addGroup" v-b-popover.hover.top="'create new group'" class="nav-icon" ></b-icon-plus> -->
            <b-button v-if="isAdmin" class="mr-1" size="sm" @click="$bvModal.show('element-key-list')" v-b-popover.hover.top="'show key list'">
              <b-icon-list-stars></b-icon-list-stars>
            </b-button>
            <b-button v-if="isAdmin" class="mr-1" size="sm" @click="addGroup" v-b-popover.hover.top="'add group'">
              <b-icon-plus-square></b-icon-plus-square>
            </b-button>
            <b-button v-if="isAdmin" class="mr-1" size="sm" @click="$bvModal.show('save-report')" v-b-popover.hover.top="'save report'">
              <b-icon-box-arrow-in-down></b-icon-box-arrow-in-down>
            </b-button>
            <b-button class="mr-1" size="sm" @click="findReport" v-b-popover.hover.top="'find reports'">
              <b-icon-search></b-icon-search>
            </b-button>
            <b-button class="mr-1" size="sm" @click="createPdf" v-b-popover.hover.top="'export'">
              <b-icon-file-bar-graph></b-icon-file-bar-graph>
            </b-button>
          </div>
          <div :id="row.id" class="group" :style="{ height: `${svgRowHeight - 1.5}px` }" v-for="row of groups" :key="row.id">
            <div v-if="!row.editing || !isAdmin" style="text-align: left; display: grid;">
              <h5 style="grid-column: 1 / 5;">{{ row.title }}</h5>
              <b-icon-arrows-expand
                v-if="isAdmin"
                @click="row.editing = true"
                v-b-popover.hover.top="'expand group'"
                style="grid-column: 5 / 6; cursor: pointer;"
              ></b-icon-arrows-expand>

              <div v-for="line of row.groupRows" :key="line.id" class="group-row">
                <div style="grid-column: 1 / 5;">{{ line.legend }}</div>
                <b-icon-eye-fill
                  v-show="line.visible"
                  style="grid-column: 7 / 8;"
                  @click="toggleLineVisibilty(row.id, line.id)"
                  class="line-icon"
                ></b-icon-eye-fill>
                <b-icon-eye-slash-fill
                  v-show="!line.visible"
                  style="grid-column: 7 / 8;"
                  @click="toggleLineVisibilty(row.id, line.id)"
                  class="line-icon"
                ></b-icon-eye-slash-fill>
              </div>
            </div>

            <div v-if="row.editing && isAdmin">
              <b-input v-model="row.title" placeholder="title" type="text"></b-input>
              <div class="group-controls">
                <b-icon-plus-square @click="addLine(row.id)" v-b-popover.hover.top="'add line'" class="ctrl-icon"></b-icon-plus-square>
                <b-icon-trash-fill @click="deleteGroup(row.id)" v-b-popover.hover.top="'delete group'" class="ctrl-icon"></b-icon-trash-fill>
                <b-icon-front @click="duplicateGroup(row.id)" v-b-popover.hover.top="'duplicate group'" class="ctrl-icon"></b-icon-front>
                <b-icon-arrows-collapse
                  @click="row.editing = false"
                  v-b-popover.hover.top="'collapse group'"
                  class="ctrl-icon"
                ></b-icon-arrows-collapse>
                <div>
                  <b-checkbox v-model="row.settings.absolute" inline>abs</b-checkbox>
                  <b-checkbox v-model="row.settings.ranges" inline>ranges</b-checkbox>
                </div>
              </div>
              <div v-for="line of row.groupRows" :key="line.id" class="group-row">
                <b-input style="grid-column: 1 / 6;" v-model="line.template" placeholder="template" type="text"></b-input>

                <b-icon-trash-fill style="grid-column: 6 / 7;" @click="deleteLine(row.id, line.id)" class="line-icon"></b-icon-trash-fill>

                <b-icon-eye-fill
                  v-show="line.visible"
                  style="grid-column: 7 / 8;"
                  @click="toggleLineVisibilty(row.id, line.id)"
                  class="line-icon"
                ></b-icon-eye-fill>
                <b-icon-eye-slash-fill
                  v-show="!line.visible"
                  style="grid-column: 7 / 8;"
                  @click="toggleLineVisibilty(row.id, line.id)"
                  class="line-icon"
                ></b-icon-eye-slash-fill>

                <!-- <b-icon-info-circle style="grid-column: 7 / 8; grid-row: 2" class="line-icon" @click="set"  ></b-icon-info-circle> -->

                <b-input style="grid-column: 1 / 6; grid-row: 2" v-model="line.legend" type="text" placeholder="legend"></b-input>

                <b-select style="grid-column: 6 / 8; grid-row: 2" :options="lineIconOptions" v-model="line.icon"></b-select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal size="lg" id="element-key-list" title="Element Key Copy Paste List" hide-footer>
      <b-table small striped :items="keyList"></b-table>
    </b-modal>

    <b-modal id="save-report" title="Save Report" hide-footer centered>
      <b-form style="text-align: left;" @submit.prevent="saveReport">
        <b-form-group v-if="!currentReport.id" label="Report Name:">
          <b-input required v-model="reportName" type="text" placeholder="report name"></b-input>
        </b-form-group>

        <b-button size="sm" class="mr-1" type="submit">Save</b-button>
        <b-button size="sm" v-show="currentReport.id" @click="currentReport.id = null">Create New</b-button>
      </b-form>
    </b-modal>

    <b-modal size="lg" id="report-list" title="Report List" hide-footer centered>
      <b-table :items="reportList" :fields="['reportName', 'createdDate', 'createdBy']">
        <template v-slot:cell(createdDate)="row">{{ new Date(row.item.createdDate).toLocaleDateString() }}</template>
        <template v-slot:cell(reportName)="row"
          ><b-link @click="setCurrentReport(row.item)">{{ row.item.reportName }}</b-link></template
        >
      </b-table>
    </b-modal>

    <hover-list :groups="groups" @reorderGroups="reorderGroups" />
  </div>
</template>

<script>
import {
  BIconArrowsCollapse,
  BIconArrowsExpand,
  BIconBoxArrowInDown,
  BIconEyeFill,
  BIconEyeSlashFill,
  BIconFileBarGraph,
  BIconFront,
  BIconListStars,
  BIconPlusSquare,
  BIconSearch,
  BIconTrashFill,
} from "bootstrap-vue";
import { line, scaleLinear, scaleTime } from "d3";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { pdf, report } from "../modules/api";
import { capitalCase, dynamicSortDate, randomId } from "../modules/utils";
// import Alert from "./Alert.vue";
import chroma from "chroma-js";
import { lunarPhaseAndIcon } from "../modules/moonphase";
import DataSetHeader from "./DataSetHeader.vue";
import HoverList from "./HoverList.vue";
import ProductGraph from "./ProductGraph.vue";

export default {
  name: "main-graph",
  components: {
    BIconTrashFill,
    BIconPlusSquare,
    BIconEyeFill,
    BIconEyeSlashFill,
    BIconFront,
    BIconArrowsCollapse,
    BIconArrowsExpand,
    DataSetHeader,
    ProductGraph,
    HoverList,
    BIconBoxArrowInDown,
    BIconListStars,
    BIconSearch,
    BIconFileBarGraph,
  },
  data() {
    return {
      windowWidth: 0,
      windowHeight: 0,
      svgRowHeight: 450,
      groups: [],
      reportName: "",
      currentReport: {},
      reportList: [],
      fixed: false,
      showMoon: true,
      lineIconOptions: [
        { text: "●", value: "circle" },
        { text: "▪", value: "rect" },
        { text: "▲", value: "triangle" },
        { text: "◆", value: "diamond" },
        { text: "★", value: "star" },
      ],
    };
  },
  methods: {
    setCurrentReport(item) {
      this.groups = item.reportData.groups;
      this.currentReport = { ...item };
      this.$bvModal.hide("report-list");
    },
    async saveReport() {
      if (this.currentReport.id) {
        this.currentReport.groups = this.groups;
        let res = await report.createUpdate(this.currentReport);
        this.$bvModal.hide("save-report");
        this.notify({
          message: res.data.message,
          variant: res.data.success ? "success" : "warn",
        });
        return;
      }
      let res = await report.createUpdate({
        reportName: this.reportName,
        createdBy: this.userId,
        reportData: {
          reportName: this.reportName,
          groups: this.groups,
        },
      });
      this.$bvModal.hide("save-report");
      this.notify({
        message: res.data.message,
        variant: res.data.success ? "success" : "warn",
      });
    },
    async findReport() {
      let res = await report.get();
      if (res.data.success) {
        this.reportList = res.data.reports;
      }
      this.$bvModal.show("report-list");
    },
    addLine(id) {
      let group = this.groups.find(x => x.id === id);
      if (!group) {
        return;
      }
      group.groupRows.push({
        template: "row['sugar-y'] / row['sugar-o']",
        legend: "row['plotName'] + ' Y/O'",
        id: randomId(),
        visible: true,
        icon: "rect",
      });
    },
    addGroup() {
      this.groups.unshift({
        id: randomId(),
        title: "New Group",
        editing: true,
        settings: {},
        groupRows: [
          {
            template: "row['sugar-y'] / row['sugar-o']",
            id: randomId(),
            visible: true,
          },
          {
            template: "row['sugar-y']",
            id: randomId(),
            visible: true,
          },
        ],
      });
    },
    duplicateGroup(id) {
      let group = this.groups.find(x => x.id === id);
      if (!group) {
        return;
      }
      group = { ...group };
      group.id = randomId();
      group.groupRows = group.groupRows.map(x => ({ ...x, id: randomId() }));

      let index = this.groups.findIndex(x => x.id === id);

      this.groups = [...this.groups.slice(0, index), group, ...this.groups.slice(index, this.groups.length)];
    },
    deleteGroup(id) {
      this.groups = this.groups.filter(x => x.id !== id);
    },
    deleteLine(groupId, lineId) {
      this.groups = this.groups.map(x => {
        if (x.id === groupId) {
          x.groupRows = x.groupRows.filter(z => z.id !== lineId);
          return x;
        } else {
          return x;
        }
      });
    },
    toggleLineVisibilty(groupId, lineId) {
      this.groups = this.groups.map(x => {
        if (x.id === groupId) {
          x.groupRows = x.groupRows.map(z => {
            if (z.id === lineId) {
              z.visible = !z.visible;
              return z;
            }
            return z;
          });
          return x;
        } else {
          return x;
        }
      });
    },
    onResize() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = document.getElementById("main-svg").clientWidth;
    },
    textOffset(index, text) {
      if (index % 2 == 0) {
        let offset = text.length * 6.75;
        return -7 - offset;
      }
      return 5;
    },
    groupLines(svgRow) {
      let allData = this.convertedData;
      if (allData.length <= 0) {
        return [];
      }

      let absolute = svgRow.settings && svgRow.settings.absolute;
      const paths = [];
      let x, y;

      if (absolute) {
        ({ x, y } = this.getAbsoluteXY(svgRow, allData));
      }

      let colors = this.chromaColors;
      let colorI = 0;
      for (let mainData of allData) {
        for (let svgLine of svgRow.groupRows) {
          if (!svgLine.template || !svgLine.visible) {
            continue;
          }

          let data = mainData.data.map(row => {
            let value = 0;
            try {
              value = eval(svgLine.template);
              if (isNaN(value)) {
                value = 0;
              }
            } catch (err) {
              // return 0;
            }

            return { value, date: new Date(row.date) };
          });

          if (!absolute) {
            ({ x, y } = this.createScales(data));
          }

          let { isSimpleLine, dashedLine, lineColor } = this.lineType(svgLine, mainData);

          let style = { stroke: isSimpleLine ? lineColor.color : colors[colorI] };
          if (dashedLine) {
            style["stroke-dasharray"] = "6,3";
          }

          paths.push({
            data: line()
              // .curve(curveNatural)
              .x((d, i) => x(d.date))
              .y(d => y(parseFloat(d.value)))(data),
            id: randomId(),
            style,
          });
          colorI++;
        }
      }
      return paths;
    },
    lineType(svgLine, mainData) {
      let lineColor = this.allActiveSetData.find(x => x.id === mainData.id);
      if (!lineColor) {
        lineColor = { color: "darkgreen" };
      }

      let isYoungLine = svgLine.template.match(/-y'/g);
      let isOldLine = svgLine.template.match(/-o'/g);

      let isSimpleLine = !(isYoungLine && isOldLine);

      let dashedLine = isSimpleLine && isYoungLine;
      return { isSimpleLine, dashedLine, lineColor };
    },
    getAbsoluteXY(svgRow, allData) {
      const range = this.currentRowRange(svgRow);
      let allValues = [{ value: range["Very High"] || 0 }, { value: range["Very Low"] || 0 }];
      for (let svgLine of svgRow.groupRows) {
        if (!svgLine.template || !svgLine.visible) {
          continue;
        }
        let data = allData
          .map(x => x.data)
          .flat(1)
          .map(row => {
            let value = 0;
            try {
              value = eval(svgLine.template);
              if (isNaN(value)) {
                value = 0;
              }
            } catch (err) {
              // return 0;
            }

            return { value, date: new Date(row.date) };
          });
        allValues.push(data);
      }
      const returnData = this.createScales(allValues.flat(1));
      returnData.range = range;
      return returnData;
    },
    createScales(data) {
      const dates = this.uniqueDates;
      const x = scaleTime()
        .domain([Math.min(...dates), Math.max(...dates)])
        .range([160, this.windowWidth - 40]);
      let values = data.map(x => parseFloat(x.value));

      const y = scaleLinear()
        .domain([Math.max(...values), Math.min(...values)])
        .range([10, this.svgRowHeight - 20]);
      return { x, y };
    },
    elementCircles(svgRow) {
      let allData = this.convertedData;
      if (allData.length <= 0) {
        return [];
      }

      let absolute = svgRow.settings && svgRow.settings.absolute;
      const circles = [];
      let x, y;

      if (absolute) {
        ({ x, y } = this.getAbsoluteXY(svgRow, allData));
      }

      let colors = this.chromaColors;

      let colorI = 0;
      for (let mainData of allData) {
        let index = 0;
        for (let svgLine of svgRow.groupRows) {
          if (!svgLine.template || !svgLine.visible) {
            continue;
          }

          let data = mainData.data.map(row => {
            let value = 0;
            try {
              value = eval(svgLine.template);
              if (isNaN(value)) {
                value = 0;
              }
            } catch (err) {
              // return 0;
            }

            return { value, date: new Date(row.date) };
          });

          if (!absolute) {
            ({ x, y } = this.createScales(data));
          }
          let lineColor = this.allActiveSetData.find(x => x.id === mainData.id);
          if (!lineColor) {
            lineColor = { color: colors[colorI] };
          }

          for (let val of data) {
            circles.push({
              circleX: x(val.date),
              circleY: y(val.value),
              text: parseFloat(val.value).toFixed(2),
              color: lineColor.color,
              icon: svgLine.icon,
              index,
            });
            index++;
          }

          colorI++;
        }
      }
      return circles;
    },

    ranges(svgRow) {
      let allData = this.convertedData;
      if (allData.length <= 0 || !this.currentCrop) {
        return [];
      }

      let { y, range } = this.getAbsoluteXY(svgRow, allData);

      return {
        perfect: y(range.Perfect),
        oky: y(range["Ok High"]),
        okh: y(range["Ok Low"]) - y(range["Ok High"]),
        veryy: y(range["Very High"]),
        veryh: y(range["Very Low"]) - y(range["Very High"]),
      };
    },
    currentRowRange(svgRow) {
      let element = this.elements.find(x => {
        for (let row of svgRow.groupRows) {
          if (!row.template?.includes(x.key)) {
            return false;
          }
          return true;
        }
      });
      if (!element) {
        return {};
      }
      return this.currentSeriesRange(element.key);
    },
    currentSeriesRange(element) {
      if (!this.currentCrop) {
        return [];
      }
      const ranges = this.getRanges.find(x => x.cropName == this.currentCrop);
      const elmtRange = {};
      if (ranges) {
        for (let name of ranges.names) {
          elmtRange[name] = ranges[name][element];
        }
      }
      return elmtRange;
    },

    legend(svgRow) {
      let allData = this.convertedData;
      if (allData.length <= 0) {
        return [];
      }

      let colors = this.chromaColors;
      let legends = [];

      let colorI = 0;
      for (let mainData of allData) {
        for (let svgLine of svgRow.groupRows) {
          if (!svgLine.template || !svgLine.visible) {
            continue;
          }

          let { isSimpleLine, dashedLine, lineColor } = this.lineType(svgLine, mainData);

          let style = { stroke: isSimpleLine ? lineColor.color : colors[colorI] };
          if (dashedLine) {
            style["stroke-dasharray"] = "6,3";
          }

          let row = mainData.data[0];
          let legend;
          try {
            legend = eval(svgLine.legend);
          } catch (error) {
            legend = svgLine.legend;
          }

          legends.push({
            id: randomId(),
            legend,
            visible: svgLine.visible,
            style,
          });

          colorI++;
        }
      }

      return legends;
    },
    setFixed(evt) {
      if (evt.target.scrollTop > 100) {
        this.fixed = true;
      } else {
        this.fixed = false;
      }
    },
    moonIcon(d) {
      return lunarPhaseAndIcon(new Date(d.text));
    },
    reorderGroups(groups) {
      this.groups = groups;
    },
    async createPdf() {
      const headSvg = document.getElementById("main-graph-dates");
      const mainSvg = document.getElementById("main-svg");

      let svgHeader = headSvg.outerHTML;
      let ratio = 700 / 989;
      // eslint-disable-next-line quotes
      svgHeader = svgHeader.replace('height="55"', `viewBox='0 0 ${this.windowWidth} ${this.windowWidth / ratio}'`);
      svgHeader = svgHeader.replace("</svg>", "");
      const svgList = [];
      let currentSvg = svgHeader;
      let currentHeight = 0;
      let pageIndex = 0;
      let index = 0;
      let pdfGroupIndex = 0;
      for (let child of mainSvg.children) {
        currentHeight += this.svgRowHeight;
        if (currentHeight >= this.windowWidth / ratio) {
          currentSvg += "</svg>";
          svgList.push(currentSvg);
          currentSvg = svgHeader;
          currentHeight = this.svgRowHeight;
          pageIndex++;
          pdfGroupIndex = 0;
        }
        if (pageIndex > 0) {
          currentSvg += child.outerHTML.replace(
            `transform="translate(0,${this.graphRows(index)})"`,
            `transform="translate(0,${this.graphRows(pdfGroupIndex)})"`
          );
        } else {
          currentSvg += child.outerHTML;
        }
        index++;
        pdfGroupIndex++;
      }
      currentSvg += "</svg>";
      svgList.push(currentSvg);

      let res = await pdf.createLinePdf({ svgs: svgList });

      const blob = new Blob([res.data], { type: "application/pdf" });

      // process to auto download it
      const fileURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      document.body.append(link);
      link.href = fileURL;
      link.download = `AdvancedGraph-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      setTimeout(function() {
        // For Firefox it is necessary to delay revoking the ObjectURL
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(link);
      }, 100);
    },
    ...mapMutations(["closePage"]),
    ...mapActions(["notify"]),
  },
  computed: {
    graphRows() {
      return function(rowNum) {
        let start = 60;
        return rowNum * this.svgRowHeight + start;
      };
    },
    chromaColors() {
      let maxGroupLines = Math.max(...this.groups.map(x => x.groupRows.length));
      let dataLength = this.allActiveSetData.length;
      return chroma.scale(chroma.brewer.Set1).colors(maxGroupLines * dataLength);
    },
    uniqueDates() {
      return Array.from(
        new Set(
          this.allActiveSetData
            .map(x => x.data)
            .flat(2)
            .map(x => x.sampleDate)
        )
      ).map(x => new Date(x));
    },

    currentCrop() {
      let crop = Array.from(new Set(this.allActiveSetData.map(x => x.data.map(z => z.cropName)).flat()));
      if (crop.length == 1) {
        return crop[0];
      }
      return null;
    },

    convertedData() {
      let masterList = [];

      for (let row of this.allActiveSetData) {
        let allRecords = row.data;

        let young = allRecords.filter(x => x.plantPart?.includes("young"));
        let old = allRecords.filter(x => x.plantPart?.includes("old"));
        let middle = allRecords.filter(x => x.plantPart?.includes("middle"));

        const dates = Array.from(new Set(allRecords.map(x => new Date(x.sampleDate).toLocaleDateString())));

        const data = dates.map(date => {
          const row = {
            date,
          };

          let youngVal = young.filter(x => new Date(x.sampleDate).toLocaleDateString() === date);
          let oldVal = old.filter(x => new Date(x.sampleDate).toLocaleDateString() === date);
          let middleVal = middle.filter(x => new Date(x.sampleDate).toLocaleDateString() === date);
          for (let elmt of this.elements) {
            let y = youngVal.map(getElementValue(elmt));
            let o = oldVal.map(getElementValue(elmt));
            let m = middleVal.map(getElementValue(elmt));

            if (parseFloat(y[0])) {
              row[`${elmt.key}-y`] = parseFloat(y[0]);
            }

            if (parseFloat(o[0])) {
              row[`${elmt.key}-o`] = parseFloat(o[0]);
            }

            if (parseFloat(m[0])) {
              row[`${elmt.key}-m`] = parseFloat(m[0]);
            }
          }

          const additionKeys = ["businessName", "locationName", "plotName", "cropName", "varietyName"];
          for (let key of additionKeys) {
            if (youngVal[0]) {
              row[key] = youngVal[0][key];
            } else if (oldVal[0]) {
              row[key] = oldVal[0][key];
            }
          }

          return row;
        });

        masterList.push({ id: row.id, data: data.sort(dynamicSortDate("date")) });
      }
      return masterList;
    },
    dateLines() {
      const { x } = this.createScales([]);

      const dates = this.uniqueDates;
      let dateList = [];

      for (let date of dates) {
        dateList.push({
          x: x(date),
          text: date.toLocaleDateString(),
          id: randomId(),
        });
      }

      return dateList;
    },
    keyList() {
      const list = this.elements.map(x => ({
        element: x.label,
        young: `row['${x.key}-y']`,
        old: `row['${x.key}-o']`,
      }));
      const additionKeys = ["businessName", "locationName", "plotName", "cropName", "varietyName"];

      return list.concat(
        additionKeys.map(x => ({
          element: capitalCase(x),
          young: `row['${x}']`,
        }))
      );
    },

    ...mapGetters({
      addresses: "currentAddresses",
      searchResults: "currentSearchResults",
      allRecords: "allRecords",
      getRanges: "getRanges",
      elements: "elements",
      userId: "userId",
      isAdmin: "isAdmin",
    }),
    ...mapGetters(["allActiveSetData", "mainGraph"]),
  },
  mounted() {
    window.addEventListener("resize", this.onResize);
    this.onResize();

    let startGroups = this.elements.map(x => ({
      id: randomId(),
      title: x.label,
      editing: true,
      settings: {
        absolute: true,
        ranges: true,
      },
      groupRows: [
        {
          template: `row['${x.key}-y']`,
          id: randomId(),
          visible: true,
          legend: "row['plotName']",
          icon: "circle",
        },
        {
          template: `row['${x.key}-o']`,
          id: randomId(),
          visible: true,
          legend: "row['plotName']",
          icon: "circle",
        },
      ],
    }));

    this.groups = startGroups;

    let page = document.getElementById("main-graph-scroll");
    page.addEventListener("scroll", this.setFixed);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    let page = document.getElementById("main-graph-scroll");
    page.removeEventListener("scroll", this.setFixed);
  },
  watch: {
    async mainGraph(newVal) {
      if (newVal) {
        await this.$nextTick();
        this.onResize();
      }
    },
  },
};

function getElementValue(elmt) {
  return d => {
    let value = d.values.find(c => c.name === elmt.key);
    return value ? value.value : null;
  };
}
</script>

<style lang="scss" scoped>
#main-svg {
  background-color: white;
  border: 1px solid #ccc;
  box-shadow: 0px 0px 2px darkgray;
  border-radius: 0.25em;
}

.product-graph {
  position: fixed;
  bottom: 2px;
}

.date-lines {
  position: absolute;
  width: 79%;
  z-index: 2;
}

.date-lines-fixed {
  position: fixed;
  width: 97.5%;
  top: 8.5em;
  background-color: rgb(255, 255, 255);
  border-radius: 4px;
  box-shadow: 0px 3px 3px darkgray;
}

.data-number {
  display: inline-block;
  margin-right: 0.6em;
  font-size: 1.75em;
  font-weight: bold;
  margin-left: -0.5em;
}

.main-grid-cont {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 5px;
  grid-auto-rows: minmax(100px, auto);
}

.svg-cont {
  grid-column: 1 / 9;
}

.settings {
  background-color: white;
  // padding: .5em;
  grid-column: 9 / 11;
  border: 1px solid #ccc;
  box-shadow: 0px 0px 2px darkgray;
  border-radius: 0.25em;
}

.group {
  border: 1px solid #ccc;
  // box-shadow: 0px 0px 2px darkgray;
  border-radius: 0.25em;
  margin: 0.1em;
  padding: 0.2em;
  overflow: auto;
}

.group-controls {
  display: block;
  text-align: left;
  padding: 0.5em;
}

.group-row {
  grid-column: 1 / 6;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  padding: 0.25em;
  background-color: bisque;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1px;
}

.setting-nav {
  background-color: rgb(253, 245, 226);
  // height: 3em;
  padding: 0.2em;
  width: 100%;
  display: block;
  text-align: left;
  margin-bottom: 1.2em;
}

.nav-icon {
  font-size: 2.75em;
  color: rgb(110, 96, 62);
  cursor: pointer;
}

.ctrl-icon {
  font-size: 1.4em;
  margin: 0.1em;
  color: rgb(100, 86, 38);
  cursor: pointer;
}

.line-icon {
  font-size: 1.2em;
  margin: auto;
  color: rgb(100, 86, 38);
  cursor: pointer;
}
</style>
